import { useState, useRef, useCallback, useEffect, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDragAndDrop } from '@formkit/drag-and-drop/react'
import {
  MessageSquare, Paperclip, Calendar, AlertCircle,
  ChevronLeft, ChevronRight, Inbox,
  Bug, Sparkles, TrendingUp, Wrench, Search, Circle,
} from 'lucide-react'

const priorityConfig = {
  1: { label: 'Low', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  2: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  3: { label: 'High', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  4: { label: 'Critical', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
}

// Per-type rendering: icon, left-border accent, and badge colors.
const typeConfig = {
  1: { label: 'Bug', Icon: Bug, accent: 'border-l-red-400', badge: 'text-red-600 bg-red-50', icon: 'text-red-500' },
  2: { label: 'Feature', Icon: Sparkles, accent: 'border-l-purple-400', badge: 'text-purple-600 bg-purple-50', icon: 'text-purple-500' },
  3: { label: 'Improvement', Icon: TrendingUp, accent: 'border-l-amber-400', badge: 'text-amber-600 bg-amber-50', icon: 'text-amber-500' },
  4: { label: 'Maintenance', Icon: Wrench, accent: 'border-l-gray-400', badge: 'text-gray-600 bg-gray-100', icon: 'text-gray-500' },
  5: { label: 'Investigation', Icon: Search, accent: 'border-l-blue-400', badge: 'text-blue-600 bg-blue-50', icon: 'text-blue-500' },
  6: { label: 'Other', Icon: Circle, accent: 'border-l-slate-300', badge: 'text-gray-500 bg-gray-50', icon: 'text-slate-400' },
}

// Column order left → right. Backlog and Closed are collapsible narrow drop columns.
const STATUS_COLUMNS = [
  { key: 'backlog', label: 'Backlog', status: 6, color: 'bg-slate-400', headerBg: 'bg-slate-50', headerBorder: 'border-slate-200', collapsible: true, position: 'start' },
  { key: 'open', label: 'Open', status: 1, color: 'bg-blue-500', headerBg: 'bg-blue-50', headerBorder: 'border-blue-200' },
  { key: 'inProgress', label: 'In Progress', status: 2, color: 'bg-primary-500', headerBg: 'bg-orange-50', headerBorder: 'border-orange-200' },
  { key: 'onHold', label: 'On Hold', status: 3, color: 'bg-yellow-500', headerBg: 'bg-yellow-50', headerBorder: 'border-yellow-200' },
  { key: 'resolved', label: 'Resolved', status: 4, color: 'bg-green-500', headerBg: 'bg-green-50', headerBorder: 'border-green-200' },
  { key: 'closed', label: 'Closed', status: 5, color: 'bg-gray-400', headerBg: 'bg-gray-50', headerBorder: 'border-gray-200', collapsible: true, position: 'end' },
]

const COLLAPSED_STORAGE_KEY = 'kanban_collapsedColumns'

const getInitialCollapsed = () => {
  try {
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY)
    if (stored) return new Set(JSON.parse(stored))
  } catch { /* ignore */ }
  return new Set(['backlog', 'closed'])
}

const scopeFlags = (scope) => {
  const parts = []
  if (scope & 1) parts.push('API')
  if (scope & 2) parts.push('Mobile')
  if (scope & 4) parts.push('Portal')
  return parts
}

const formatDate = (dateString) => {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const isTaskOverdue = (task) =>
  task.dueDate && new Date(task.dueDate) < new Date() &&
  task.status !== 4 && task.status !== 5 && task.status !== 6

const KanbanCard = ({ task }) => {
  const navigate = useNavigate()
  const overdue = isTaskOverdue(task)
  const priority = priorityConfig[task.priority] || priorityConfig[1]
  const type = typeConfig[task.taskType] || typeConfig[6]
  const TypeIcon = type.Icon
  const scopes = scopeFlags(task.projectScope)

  return (
    <div
      data-task-id={task.supportTaskId}
      onClick={() => navigate(`/tasks/${task.supportTaskId}`)}
      className={`group relative bg-white rounded-xl border border-gray-200/80 border-l-[3px] p-3.5 cursor-grab active:cursor-grabbing hover:shadow-lg hover:border-gray-300 transition-all select-none shadow-sm ${overdue ? 'border-l-red-400' : type.accent}`}
    >
      {/* Top row: type icon + task number + priority */}
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <TypeIcon size={13} className={`${type.icon} flex-shrink-0`} />
          <span className="text-[11px] font-mono text-gray-400 tracking-wide truncate">{task.taskNumber}</span>
        </span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold flex-shrink-0 ${priority.bg} ${priority.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
          {priority.label}
        </span>
      </div>

      {/* Title */}
      <h4 className="text-[13px] font-semibold text-gray-900 line-clamp-2 mb-2.5 leading-snug group-hover:text-primary-700 transition-colors">
        {task.title}
      </h4>

      {/* Tags row: type + scope */}
      <div className="flex flex-wrap gap-1 mb-3">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${type.badge}`}>
          {type.label}
        </span>
        {scopes.map((s) => (
          <span key={s} className="px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500 bg-gray-100">
            {s}
          </span>
        ))}
      </div>

      {/* Footer: assignee, counts, due date */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          {task.assignedToName ? (
            <span
              className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 shadow-sm"
              title={task.assignedToName}
            >
              {task.assignedToName[0].toUpperCase()}
            </span>
          ) : (
            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-[10px] flex-shrink-0" title="Unassigned">
              ?
            </span>
          )}
          {task.assignedToName && (
            <span className="text-[11px] text-gray-500 truncate max-w-[80px]">{task.assignedToName.split(' ')[0]}</span>
          )}
        </div>
        <div className="flex items-center gap-2.5 text-gray-400">
          {task.commentCount > 0 && (
            <span className="flex items-center gap-0.5 text-[10px]" title={`${task.commentCount} comments`}>
              <MessageSquare size={11} />
              <span>{task.commentCount}</span>
            </span>
          )}
          {task.attachmentCount > 0 && (
            <span className="flex items-center gap-0.5 text-[10px]" title={`${task.attachmentCount} attachments`}>
              <Paperclip size={11} />
              <span>{task.attachmentCount}</span>
            </span>
          )}
          {task.dueDate && (
            <span className={`flex items-center gap-0.5 text-[10px] ${overdue ? 'text-red-500 font-semibold' : ''}`} title={overdue ? 'Overdue!' : `Due ${formatDate(task.dueDate)}`}>
              {overdue ? <AlertCircle size={11} /> : <Calendar size={11} />}
              <span>{formatDate(task.dueDate)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const KanbanColumn = memo(({
  column, tasks: initialTasks, reorderEnabled, collapsible,
  onToggleCollapse, onReorder, onStatusChange, draggedTaskRef,
}) => {
  const { key: columnKey, status, label, color, headerBg, headerBorder } = column
  const prevTasksRef = useRef(JSON.stringify(initialTasks.map((t) => t.supportTaskId)))
  const debounceRef = useRef(null)
  const suppressSyncRef = useRef(false)
  // Baseline tracks the last committed order; updates after each reorder fires so
  // rapid successive drags each compare against the post-previous-drag state.
  const baselineRef = useRef(initialTasks.map((t) => t.supportTaskId))

  const [listRef, items, setItems] = useDragAndDrop(initialTasks, {
    group: reorderEnabled ? 'kanban-tasks' : undefined,
  })

  // Sync parent tasks -> internal items when parent data changes (not during an active drag).
  useEffect(() => {
    if (suppressSyncRef.current) return
    const newIds = JSON.stringify(initialTasks.map((t) => t.supportTaskId))
    if (newIds !== prevTasksRef.current) {
      prevTasksRef.current = newIds
      baselineRef.current = initialTasks.map((t) => t.supportTaskId)
      setItems(initialTasks)
    }
  }, [initialTasks, setItems])

  // Debounced reorder detection.
  useEffect(() => {
    const currentIds = items.map((t) => t.supportTaskId)
    const currentStr = JSON.stringify(currentIds)
    const baselineStr = JSON.stringify(baselineRef.current)
    if (currentStr === baselineStr) return

    suppressSyncRef.current = true
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      suppressSyncRef.current = false
      const baseline = baselineRef.current
      const baselineIds = new Set(baseline)

      // Incoming task (cross-column drop)
      for (let i = 0; i < currentIds.length; i++) {
        if (!baselineIds.has(currentIds[i])) {
          baselineRef.current = currentIds
          if (onReorder && reorderEnabled) onReorder(currentIds[i], status, i)
          else if (onStatusChange) onStatusChange(currentIds[i], status)
          return
        }
      }

      // Within-column reorder
      if (reorderEnabled && onReorder) {
        for (let i = 0; i < currentIds.length; i++) {
          if (currentIds[i] !== baseline[i]) {
            baselineRef.current = currentIds
            onReorder(currentIds[i], status, i)
            return
          }
        }
      }
    }, 500)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [items, status, reorderEnabled, onReorder, onStatusChange])

  return (
    <div className="w-[300px] flex-shrink-0 flex flex-col">
      {/* Column header */}
      <div className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${headerBg} ${headerBorder} mb-3`}>
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${color} shadow-sm`} />
          <h3 className="text-sm font-bold text-gray-800">{label}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-gray-500 bg-white/70 px-2 py-0.5 rounded-full min-w-[24px] text-center shadow-sm">
            {items.length}
          </span>
          {collapsible && (
            <button
              onClick={() => onToggleCollapse(columnKey)}
              className="p-0.5 text-gray-400 hover:text-gray-700 rounded transition-colors"
              title="Collapse column"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Column body (drag list). The empty-state placeholder is an overlay, not a child of
          the drag container: the library counts every child of that container as a card, so a
          placeholder in an empty column leaves it unregistered and the next drop throws. */}
      <div className="relative flex-1 flex flex-col">
        <div
          ref={listRef}
          data-column={columnKey}
          onDragStartCapture={(e) => {
            const card = e.target.closest('[data-task-id]')
            if (card && draggedTaskRef) draggedTaskRef.current = parseInt(card.dataset.taskId, 10)
          }}
          onDragEndCapture={() => { if (draggedTaskRef) draggedTaskRef.current = null }}
          className="flex-1 rounded-xl p-2 space-y-2.5 min-h-[300px] max-h-[calc(100vh-300px)] overflow-y-auto bg-gray-50/80"
        >
          {items.map((task) => (
            <KanbanCard key={task.supportTaskId} task={task} />
          ))}
        </div>
        {items.length === 0 && (
          <div className="pointer-events-none absolute inset-x-2 top-2 flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 gap-1.5">
            <Inbox size={22} />
            <span className="text-xs font-medium">No tasks</span>
          </div>
        )}
      </div>
    </div>
  )
})

KanbanColumn.displayName = 'KanbanColumn'

const CollapsedColumn = ({ column, count, empty, isDropTarget, onExpand, onDragEnter, onDragLeave, onDragOver, onDrop }) => {
  const { color, label, position } = column
  return (
    <div
      onClick={() => !empty && onExpand()}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`w-10 flex-shrink-0 sticky ${position === 'start' ? 'left-0' : 'right-0'} z-10 flex flex-col items-center bg-white border rounded-xl overflow-hidden transition-all ${empty ? 'opacity-40 cursor-default border-gray-200' : 'cursor-pointer'} ${isDropTarget ? 'border-primary-400 bg-primary-50/60' : 'border-gray-200 hover:border-gray-300'}`}
      title={empty ? `${label} (empty)` : `Expand ${label}`}
    >
      <div className={`w-full h-1 ${color}`} />
      <div className="flex-1 flex items-center [writing-mode:vertical-rl] rotate-180 text-[11px] font-bold uppercase tracking-wide text-gray-500 py-3">
        {label}
      </div>
      <span className="mb-2 text-[10px] font-bold text-gray-500 bg-gray-100 w-5 h-5 flex items-center justify-center rounded-full">
        {count}
      </span>
      {!empty && <ChevronRight size={14} className="mb-2 text-gray-400" />}
    </div>
  )
}

const KanbanBoard = ({ data, onStatusChange, onReorder, sortBy = 'custom' }) => {
  const [collapsedColumns, setCollapsedColumns] = useState(getInitialCollapsed)
  const dragCounter = useRef({})
  const [dragOverColumn, setDragOverColumn] = useState(null)
  const draggedTaskRef = useRef(null)

  const reorderEnabled = sortBy === 'custom'

  const toggleCollapse = useCallback((key) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      try { localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }, [])

  const sortColumnTasks = useCallback((columnTasks) => {
    if (sortBy === 'custom') return columnTasks // server-provided SortOrder
    const sorted = [...columnTasks]
    if (sortBy === 'priority') {
      sorted.sort((a, b) => {
        const diff = (b.priority ?? 0) - (a.priority ?? 0)
        if (diff !== 0) return diff
        return new Date(b.createDateTimeUtc) - new Date(a.createDateTimeUtc)
      })
    } else if (sortBy === 'dueDate') {
      sorted.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0
        if (!a.dueDate) return 1
        if (!b.dueDate) return -1
        return new Date(a.dueDate) - new Date(b.dueDate)
      })
    } else if (sortBy === 'created') {
      sorted.sort((a, b) => new Date(b.createDateTimeUtc) - new Date(a.createDateTimeUtc))
    }
    return sorted
  }, [sortBy])

  const handleCollapsedDragEnter = (e, columnKey) => {
    e.preventDefault()
    dragCounter.current[columnKey] = (dragCounter.current[columnKey] || 0) + 1
    setDragOverColumn(columnKey)
  }
  const handleCollapsedDragLeave = (columnKey) => {
    dragCounter.current[columnKey] = (dragCounter.current[columnKey] || 0) - 1
    if (dragCounter.current[columnKey] <= 0) {
      dragCounter.current[columnKey] = 0
      setDragOverColumn((prev) => (prev === columnKey ? null : prev))
    }
  }
  const handleCollapsedDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
  const handleCollapsedDrop = (e, column) => {
    e.preventDefault()
    dragCounter.current[column.key] = 0
    setDragOverColumn(null)
    const taskId = draggedTaskRef.current
    draggedTaskRef.current = null
    if (taskId) {
      if (onReorder && reorderEnabled) onReorder(taskId, column.status, 0)
      else if (onStatusChange) onStatusChange(taskId, column.status)
    }
    if (collapsedColumns.has(column.key)) toggleCollapse(column.key)
  }

  if (!data) return null

  const columnData = STATUS_COLUMNS.map((col) => ({
    ...col,
    tasks: sortColumnTasks(data[col.key] || []),
  }))

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
      {columnData.map((col) => {
        const isEmpty = col.tasks.length === 0
        const isCollapsed = col.collapsible && (collapsedColumns.has(col.key) || isEmpty)

        if (isCollapsed) {
          return (
            <CollapsedColumn
              key={col.key}
              column={col}
              count={col.tasks.length}
              empty={isEmpty}
              isDropTarget={dragOverColumn === col.key}
              onExpand={() => toggleCollapse(col.key)}
              onDragEnter={(e) => handleCollapsedDragEnter(e, col.key)}
              onDragLeave={() => handleCollapsedDragLeave(col.key)}
              onDragOver={handleCollapsedDragOver}
              onDrop={(e) => handleCollapsedDrop(e, col)}
            />
          )
        }

        return (
          <KanbanColumn
            key={col.key}
            column={col}
            tasks={col.tasks}
            reorderEnabled={reorderEnabled}
            collapsible={col.collapsible}
            onToggleCollapse={toggleCollapse}
            onReorder={onReorder}
            onStatusChange={onStatusChange}
            draggedTaskRef={draggedTaskRef}
          />
        )
      })}
    </div>
  )
}

export default KanbanBoard
